import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Lock, Building2, Globe, MapPin, Phone, Pencil, Loader2, Key, Cpu, Link2, Unlink } from "lucide-react";
import ChangePasswordModal from "./ChangePasswordModal";
import { VerifyContext } from "../../app/VerifyContext";
import api from "api/axios";
import { fetchLLMProviderSettings, updateLLMProviderSetting, fetchUpworkConnection, startUpworkConnect, disconnectUpwork } from "./api/SettingsApi";
import type { LLMProvider, LLMProviderSetting, UpworkConnection } from "./api/SettingsApi";

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}){1,2}$/;

type ColorSchemeFields = {
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
};

const COLOR_SCHEME_FIELDS: { key: keyof ColorSchemeFields; label: string }[] = [
  { key: "primary_color", label: "Primary Color" },
  { key: "secondary_color", label: "Secondary Color" },
  { key: "accent_color", label: "Accent Color" },
  { key: "background_color", label: "Background Color" },
];

function ColorSchemeCard({ getUrl, patchUrl }: { getUrl: string; patchUrl: string }) {
  const [colors, setColors] = useState<ColorSchemeFields>({
    primary_color: "",
    secondary_color: "",
    accent_color: "",
    background_color: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(getUrl).then((res) => {
      if (!mounted) return;
      const cs = res.data.color_scheme || {};
      setColors({
        primary_color: cs.primary_color || "",
        secondary_color: cs.secondary_color || "",
        accent_color: cs.accent_color || "",
        background_color: cs.background_color || "",
      });
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [getUrl]);

  const handleChange = (key: keyof ColorSchemeFields, value: string) => {
    setColors((c) => ({ ...c, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch(patchUrl, { color_scheme: colors });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError("Failed to save color scheme. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1 font-inter">Color Scheme</h2>
      <p className="text-xs text-muted-foreground font-inter mb-5">Optional. Used to auto-fill the color_scheme placeholder in Prompt Library. Leave any field blank to omit it.</p>
      {loading ? (
        <p className="text-sm text-muted-foreground font-inter">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            {COLOR_SCHEME_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-muted-foreground font-inter mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={colors[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                    placeholder="#3B82F6"
                    className="flex-1 text-sm px-3 py-2 bg-surface-muted border border-border rounded-lg text-foreground font-inter placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <span
                    className="w-8 h-8 rounded-lg border border-border flex-shrink-0"
                    style={{ backgroundColor: HEX_COLOR_REGEX.test(colors[key]) ? colors[key] : undefined }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            ))}
          </div>
          {error && <p className="text-destructive text-xs mt-3 font-inter">{error}</p>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-5 px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            {saving ? "Saving..." : saved ? "Saved!" : "Save"}
          </button>
        </>
      )}
    </div>
  );
}

function EmployeeSettingView() {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground font-inter">Settings</h1>
        <p className="text-sm text-muted-foreground font-inter mt-1">Manage your account settings</p>
      </div>
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-5 font-inter">Security</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-inter">Password</p>
              <p className="text-sm text-muted-foreground font-inter mt-0.5">••••••••</p>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="text-sm text-primary hover:text-primary/80 font-inter font-medium transition-colors"
          >
            Change Password
          </button>
        </div>
      </div>
      <ColorSchemeCard getUrl="/employee/profile" patchUrl="/employee/profile" />
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} isEmployee />
      )}
    </div>
  );
}

function LLMProviderSettingsCard() {
  const [settings, setSettings] = useState<LLMProviderSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingFeature, setSavingFeature] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLLMProviderSettings()
      .then(setSettings)
      .catch(() => setError("Failed to load LLM provider settings"))
      .finally(() => setLoading(false));
  }, []);

  async function handleChange(feature: string, provider: LLMProvider) {
    const previousProvider = settings.find((s) => s.feature === feature)?.provider;
    setSavingFeature(feature);
    setError("");
    setSettings((prev) => prev.map((s) => (s.feature === feature ? { ...s, provider } : s)));
    try {
      await updateLLMProviderSetting(feature, provider);
    } catch (err: any) {
      if (previousProvider) {
        setSettings((prev) => prev.map((s) => (s.feature === feature ? { ...s, provider: previousProvider } : s)));
      }
      setError(err.response?.data?.detail || "Failed to update provider");
    } finally {
      setSavingFeature(null);
    }
  }

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6 flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1 font-inter">LLM Provider Selection</h2>
      <p className="text-xs text-muted-foreground font-inter mb-5">
        Choose which AI provider powers each LLM-backed feature, application-wide.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
          <p className="text-destructive text-sm font-inter">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {settings.map((setting) => (
          <div key={setting.feature} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-5 h-5 text-primary" />
              </div>
              <p className="text-sm text-foreground font-inter">{setting.label}</p>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-border bg-surface-muted p-0.5">
              {(["openai", "deepseek"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => handleChange(setting.feature, p)}
                  disabled={savingFeature === setting.feature}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium font-inter transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    setting.provider === p
                      ? p === "openai"
                        ? "bg-primary text-primary-foreground"
                        : "bg-cyan-600 text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {p === "openai" ? "OpenAI" : "DeepSeek"}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UpworkConnectionCard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<UpworkConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const refetch = () => {
    fetchUpworkConnection()
      .then(setConnection)
      .catch(() => setError("Failed to load Upwork connection status"));
  };

  useEffect(() => {
    refetch();
    setLoading(false);

    // Refetch when the user switches back from the tab where they completed
    // Upwork's consent screen — the OAuth redirect lands there, not here.
    window.addEventListener("focus", refetch);
    return () => window.removeEventListener("focus", refetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const upworkParam = searchParams.get("upwork");
    if (!upworkParam) return;
    setBanner(
      upworkParam === "connected"
        ? { type: "success", text: "Upwork account connected successfully" }
        : { type: "error", text: "Upwork authorization failed. Please try connecting again." }
    );
    refetch();
    const next = new URLSearchParams(searchParams);
    next.delete("upwork");
    setSearchParams(next, { replace: true });
    const timeout = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleConnect = async () => {
    setConnecting(true);
    setError("");
    try {
      const { authorization_url } = await startUpworkConnect();
      window.open(authorization_url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to start Upwork connection");
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError("");
    try {
      const updated = await disconnectUpwork();
      setConnection(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to disconnect Upwork");
    }
    setDisconnecting(false);
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6 flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isConnected = connection?.status === "connected";
  const statusLabel = connection?.status === "connected" ? "Connected" : connection?.status === "error" ? "Connection error" : "Not connected";
  const statusColor = isConnected ? "text-emerald-400" : connection?.status === "error" ? "text-destructive" : "text-muted-foreground";

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1 font-inter">Upwork Connection</h2>
      <p className="text-xs text-muted-foreground font-inter mb-5">
        Connect the one company-owned Upwork freelancer account used to source marketplace job postings.
      </p>

      {banner && (
        <div className={`mb-4 p-3 rounded-xl border ${banner.type === "success" ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`}>
          <p className={`text-sm font-inter ${banner.type === "success" ? "text-emerald-400" : "text-destructive"}`}>{banner.text}</p>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
          <p className="text-destructive text-sm font-inter">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            {isConnected ? <Link2 className="w-5 h-5 text-primary" /> : <Unlink className="w-5 h-5 text-primary" />}
          </div>
          <div>
            <p className={`text-sm font-inter font-medium ${statusColor}`}>{statusLabel}</p>
            {connection?.last_connected_at && (
              <p className="text-xs text-muted-foreground font-inter mt-0.5">
                Last connected {new Date(connection.last_connected_at).toLocaleString()}
              </p>
            )}
            {connection?.status === "error" && connection.last_error && (
              <p className="text-xs text-destructive font-inter mt-0.5">{connection.last_error}</p>
            )}
          </div>
        </div>
        {isConnected ? (
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-4 py-2 text-sm text-destructive hover:text-destructive/80 font-inter font-medium transition-colors disabled:opacity-50"
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </button>
        ) : (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            {connecting ? "Starting..." : "Connect Upwork"}
          </button>
        )}
      </div>
    </div>
  );
}

interface CompanyProfile {
  company_name: string;
  website: string;
  address: string;
  phone: string;
  email: string;
}

function AdminSettingView() {
  const { setUser } = useContext(VerifyContext);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [accessKeySet, setAccessKeySet] = useState(false);
  const [editingAccessKey, setEditingAccessKey] = useState(false);
  const [accessKeyInput, setAccessKeyInput] = useState("");
  const [accessKeySaving, setAccessKeySaving] = useState(false);
  const [accessKeyError, setAccessKeyError] = useState("");
  const [accessKeySuccess, setAccessKeySuccess] = useState(false);

  const [formData, setFormData] = useState<CompanyProfile>({
    company_name: "", website: "", address: "", phone: "", email: ""
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/admin/company_profile");
        setProfile(res.data);
        setFormData(res.data);
        setAccessKeySet(Boolean(res.data.access_key_set));
      } catch (error) {
        console.error("Failed to load profile:", error);
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleEdit = () => {
    setEditing(true);
    setSaveError("");
    setSaveSuccess(false);
    if (profile) setFormData(profile);
  };

  const handleCancel = () => {
    setEditing(false);
    setSaveError("");
    if (profile) setFormData(profile);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      await api.patch("/admin/update_company_profile", formData);
      setProfile(formData);
      setEditing(false);
      setSaveSuccess(true);
      setUser({ name: formData.company_name, email: formData.email });
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.response?.data?.detail || "Failed to update profile");
    }
    setSaving(false);
  };

  const handleChange = (field: keyof CompanyProfile, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAccessKeySave = async () => {
    if (!accessKeyInput.trim()) {
      setAccessKeyError("Access key cannot be empty");
      return;
    }
    setAccessKeySaving(true);
    setAccessKeyError("");
    setAccessKeySuccess(false);
    try {
      await api.patch("/admin/update_access_key", { access_key: accessKeyInput.trim() });
      setAccessKeySet(true);
      setAccessKeyInput("");
      setEditingAccessKey(false);
      setAccessKeySuccess(true);
      setTimeout(() => setAccessKeySuccess(false), 3000);
    } catch (err: any) {
      setAccessKeyError(err.response?.data?.detail || "Failed to update access key");
    }
    setAccessKeySaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const infoRows: { icon: any; label: string; field: keyof CompanyProfile; placeholder: string }[] = [
    { icon: Building2, label: "Company Name", field: "company_name", placeholder: "Acme Inc" },
    { icon: Mail, label: "Email", field: "email", placeholder: "admin@company.com" },
    { icon: Phone, label: "Phone", field: "phone", placeholder: "+92 300 1234567" },
    { icon: Globe, label: "Website", field: "website", placeholder: "https://company.com" },
    { icon: MapPin, label: "Address", field: "address", placeholder: "123 Main St, City" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground font-inter">Settings</h1>
        <p className="text-sm text-muted-foreground font-inter mt-1">Manage your company profile and account</p>
      </div>

      {/* Company Profile Card */}
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mb-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider font-inter">Company Profile</h2>
          {!editing && (
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-inter font-medium transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>

        {saveSuccess && (
          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <p className="text-emerald-400 text-sm font-inter">Profile updated successfully</p>
          </div>
        )}

        {saveError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-destructive text-sm font-inter">{saveError}</p>
          </div>
        )}

        <div className="space-y-4">
          {infoRows.map(({ icon: Icon, label, field, placeholder }) => (
            <div key={field} className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-inter">{label}</p>
                {editing ? (
                  <input
                    type="text"
                    value={formData[field]}
                    onChange={(e) => handleChange(field, e.target.value)}
                    placeholder={placeholder}
                    className="mt-1 w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                ) : (
                  <p className="text-sm text-foreground font-inter mt-0.5">{profile?.[field] || "—"}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors flex items-center gap-2"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      <ColorSchemeCard getUrl="/admin/company_profile" patchUrl="/admin/update_company_profile" />

      <LLMProviderSettingsCard />

      <UpworkConnectionCard />

      {/* Security Card */}
      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-5 font-inter">Security</h2>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-inter">Password</p>
              <p className="text-sm text-muted-foreground font-inter mt-0.5">••••••••</p>
            </div>
          </div>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="text-sm text-primary hover:text-primary/80 font-inter font-medium transition-colors"
          >
            Change Password
          </button>
        </div>

        <div className="border-t border-border mt-5 pt-5">
          {accessKeySuccess && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <p className="text-emerald-400 text-sm font-inter">Access key updated successfully</p>
            </div>
          )}
          {accessKeyError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
              <p className="text-destructive text-sm font-inter">{accessKeyError}</p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-inter">Access Key</p>
                {editingAccessKey ? (
                  <input
                    type="text"
                    value={accessKeyInput}
                    onChange={(e) => setAccessKeyInput(e.target.value)}
                    placeholder="Enter new access key"
                    className="mt-1 w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground font-inter mt-0.5 font-mono">{accessKeySet ? "••••••••" : "—"}</p>
                )}
              </div>
            </div>
            {editingAccessKey ? (
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => { setEditingAccessKey(false); setAccessKeyInput(""); setAccessKeyError(""); }} className="text-sm text-muted-foreground hover:text-foreground font-inter transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleAccessKeySave}
                  disabled={accessKeySaving}
                  className="px-4 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium font-inter rounded-lg transition-colors"
                >
                  {accessKeySaving ? "Saving..." : "Save"}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setEditingAccessKey(true); setAccessKeyInput(""); setAccessKeyError(""); setAccessKeySuccess(false); }}
                className="text-sm text-primary hover:text-primary/80 font-inter font-medium transition-colors"
              >
                Change
              </button>
            )}
          </div>
        </div>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}

export default function Setting() {
  const { superAdmin } = useContext(VerifyContext);
  return superAdmin ? <AdminSettingView /> : <EmployeeSettingView />;
}
